import React, { useState, useEffect } from 'react';
import { 
  X, 
  Mail, 
  Bell, 
  Send, 
  Eye, 
  Edit3, 
  User, 
  Palette,
  Check,
  AlertCircle,
  ChevronDown,
  ChevronUp,
  Smartphone
} from 'lucide-react';
import '../styles/PurchaseConfirmationPreview.css';

/**
 * PurchaseConfirmationPreview
 * 
 * Modal component for previewing and editing purchase confirmation emails
 * before sending to buyer and artist.
 */
const PurchaseConfirmationPreview = ({ 
  order, 
  artistInfo, 
  onClose, 
  onSend,
  sending = false 
}) => {
  const [activeTab, setActiveTab] = useState('buyer'); // 'buyer' | 'artist' | 'push'
  const [isEditing, setIsEditing] = useState(false);
  const [expandedSections, setExpandedSections] = useState({
    buyerEmail: true,
    artistEmail: true,
    pushNotifications: true
  });
  
  // Editable content state
  const [buyerEmail, setBuyerEmail] = useState({
    subject: 'Your VibrantCast Purchase Confirmation',
    header: 'Thank You for Your Purchase!',
    subheader: 'Your order has been confirmed',
    greeting: '',
    thankYouMessage: 'Thank you for supporting local art through VibrantCast! Your purchase helps support independent artists in our community.',
    fulfillmentMessage: ''
  });
  
  const [artistEmail, setArtistEmail] = useState({
    subject: '🎉 Congratulations! You made a sale!',
    header: '🎉 Congratulations!',
    greeting: 'Great news! Your artwork has been purchased through VibrantCast.',
    nextStepsMessage: ''
  });
  
  const [pushNotifications, setPushNotifications] = useState({
    buyerTitle: 'Purchase Confirmed!',
    buyerMessage: '',
    artistTitle: '🎉 You made a sale!',
    artistMessage: ''
  });

  // Initialize content based on order data
  useEffect(() => {
    if (!order) return;
    
    const productLabel = order.product_type === 'print' 
      ? `Print${order.print_size ? ` (${order.print_size})` : ''}`
      : 'Original Artwork';
    
    const artistName = artistInfo?.nickname || 
      `${artistInfo?.first_name || ''} ${artistInfo?.last_name || ''}`.trim() || 
      'the artist';
    
    // Set buyer email defaults
    const defaultBuyerFulfillment = order.product_type === 'print'
      ? 'Your print will be produced and shipped within 5-7 business days. You will receive tracking information once it ships.'
      : 'The artist has been notified of your purchase and will reach out to coordinate delivery or pickup.';
    
    setBuyerEmail(prev => ({
      ...prev,
      greeting: `Hi ${order.first_name || 'there'},`,
      fulfillmentMessage: defaultBuyerFulfillment
    }));
    
    // Set artist email defaults
    const defaultArtistNextSteps = order.product_type === 'print'
      ? 'This is a print order and will be fulfilled automatically. No action is required from you.'
      : `Please coordinate with the buyer for pickup or delivery. You can reach them at ${order.email} or through the VibrantCast app.`;
    
    setArtistEmail(prev => ({
      ...prev,
      nextStepsMessage: defaultArtistNextSteps
    }));
    
    // Set push notification defaults
    setPushNotifications({
      buyerTitle: 'Purchase Confirmed!',
      buyerMessage: `Your purchase of "${order.product_name}" is confirmed. Thank you for supporting local art!`,
      artistTitle: '🎉 You made a sale!',
      artistMessage: `"${order.product_name}" was just purchased for $${parseFloat(order.price).toFixed(2)}!`
    });
    
  }, [order, artistInfo]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const handleSend = () => {
    // Package the customized content and send
    const payload = {
      order,
      customContent: {
        buyerEmail,
        artistEmail,
        pushNotifications
      }
    };
    onSend(payload);
  };

  if (!order) return null;

  const productLabel = order.product_type === 'print' 
    ? `Print${order.print_size ? ` (${order.print_size})` : ''}`
    : 'Original Artwork';
  
  const artistName = artistInfo?.nickname || 
    `${artistInfo?.first_name || ''} ${artistInfo?.last_name || ''}`.trim() || 
    'Artist';
  
  const artistEmailAddress = artistInfo?.email || null;

  return (
    <div className="preview-modal-overlay" onClick={onClose}>
      <div className="preview-modal" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="preview-modal-header">
          <div className="preview-header-title">
            <Send size={20} />
            <h2>Purchase Confirmation Preview</h2>
          </div>
          <div className="preview-header-actions">
            <button 
              className={`edit-toggle-btn ${isEditing ? 'active' : ''}`}
              onClick={() => setIsEditing(!isEditing)}
            >
              <Edit3 size={16} />
              {isEditing ? 'Preview Mode' : 'Edit Mode'}
            </button>
            <button className="close-btn" onClick={onClose}>
              <X size={20} />
            </button>
          </div>
        </div>

        {/* Order Summary Bar */}
        <div className="preview-order-summary">
          <div className="summary-item">
            <span className="label">Order #</span>
            <span className="value">{order.order_num || 'N/A'}</span>
          </div>
          <div className="summary-item">
            <span className="label">Artwork</span>
            <span className="value">{order.product_name}</span>
          </div>
          <div className="summary-item">
            <span className="label">Amount</span>
            <span className="value">{formatCurrency(order.price)}</span>
          </div>
          <div className="summary-item">
            <span className="label">Type</span>
            <span className="value">{productLabel}</span>
          </div>
        </div>

        {/* Tabs */}
        <div className="preview-tabs">
          <button 
            className={`preview-tab ${activeTab === 'buyer' ? 'active' : ''}`}
            onClick={() => setActiveTab('buyer')}
          >
            <User size={16} />
            Buyer Email
          </button>
          <button 
            className={`preview-tab ${activeTab === 'artist' ? 'active' : ''}`}
            onClick={() => setActiveTab('artist')}
            disabled={!artistEmailAddress}
            title={!artistEmailAddress ? 'No artist email available' : ''}
          >
            <Palette size={16} />
            Artist Email
            {!artistEmailAddress && <AlertCircle size={14} className="warning-icon" />}
          </button>
          <button 
            className={`preview-tab ${activeTab === 'push' ? 'active' : ''}`}
            onClick={() => setActiveTab('push')}
          >
            <Bell size={16} />
            Push Notifications
          </button>
        </div>

        {/* Content Area */}
        <div className="preview-content">
          {/* Buyer Email Tab */}
          {activeTab === 'buyer' && (
            <div className="email-preview-container">
              <div className="email-meta">
                <div className="meta-row">
                  <span className="meta-label">To:</span>
                  <span className="meta-value">{order.email}</span>
                </div>
                <div className="meta-row">
                  <span className="meta-label">From:</span>
                  <span className="meta-value">VibrantCast &lt;support@vibrantcast.com&gt;</span>
                </div>
                <div className="meta-row">
                  <span className="meta-label">Subject:</span>
                  {isEditing ? (
                    <input 
                      type="text"
                      className="subject-input"
                      value={buyerEmail.subject}
                      onChange={(e) => setBuyerEmail({...buyerEmail, subject: e.target.value})}
                    />
                  ) : (
                    <span className="meta-value subject">{buyerEmail.subject}</span>
                  )}
                </div>
              </div>

              <div className="email-body-preview">
                <div className="email-header-section">
                  {isEditing ? (
                    <>
                      <input 
                        type="text"
                        className="header-input"
                        value={buyerEmail.header}
                        onChange={(e) => setBuyerEmail({...buyerEmail, header: e.target.value})}
                        style={{ fontSize: '1.5rem', fontWeight: 'bold', textAlign: 'center', width: '100%', marginBottom: '0.5rem' }}
                      />
                      <input 
                        type="text"
                        className="subheader-input"
                        value={buyerEmail.subheader}
                        onChange={(e) => setBuyerEmail({...buyerEmail, subheader: e.target.value})}
                        style={{ fontSize: '1rem', textAlign: 'center', width: '100%', color: '#666' }}
                      />
                    </>
                  ) : (
                    <>
                      <h1>{buyerEmail.header}</h1>
                      <p className="subtitle">{buyerEmail.subheader}</p>
                    </>
                  )}
                </div>

                {isEditing ? (
                  <input 
                    type="text"
                    className="greeting-input"
                    value={buyerEmail.greeting}
                    onChange={(e) => setBuyerEmail({...buyerEmail, greeting: e.target.value})}
                  />
                ) : (
                  <p className="greeting">{buyerEmail.greeting}</p>
                )}

                {isEditing ? (
                  <textarea 
                    className="message-textarea"
                    value={buyerEmail.thankYouMessage}
                    onChange={(e) => setBuyerEmail({...buyerEmail, thankYouMessage: e.target.value})}
                    rows={3}
                  />
                ) : (
                  <p>{buyerEmail.thankYouMessage}</p>
                )}

                <div className="order-details-table">
                  <h3>Order Details</h3>
                  <table>
                    <tbody>
                      <tr>
                        <td className="label">Artwork:</td>
                        <td className="value bold">{order.product_name}</td>
                      </tr>
                      <tr>
                        <td className="label">Artist:</td>
                        <td className="value">{artistName}</td>
                      </tr>
                      <tr>
                        <td className="label">Type:</td>
                        <td className="value">{productLabel}</td>
                      </tr>
                      <tr>
                        <td className="label">Amount:</td>
                        <td className="value bold">{formatCurrency(order.price)}</td>
                      </tr>
                      <tr>
                        <td className="label">Order #:</td>
                        <td className="value">{order.order_num}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                <div className="next-steps-section">
                  <h3>What's Next?</h3>
                  {isEditing ? (
                    <textarea 
                      className="message-textarea"
                      value={buyerEmail.fulfillmentMessage}
                      onChange={(e) => setBuyerEmail({...buyerEmail, fulfillmentMessage: e.target.value})}
                      rows={3}
                    />
                  ) : (
                    <p>{buyerEmail.fulfillmentMessage}</p>
                  )}
                </div>

                <p className="contact-note">
                  If you have any questions, you can message the artist directly through the VibrantCast app or reply to this email.
                </p>
              </div>
            </div>
          )}

          {/* Artist Email Tab */}
          {activeTab === 'artist' && (
            <div className="email-preview-container">
              {artistEmailAddress ? (
                <>
                  <div className="email-meta">
                    <div className="meta-row">
                      <span className="meta-label">To:</span>
                      <span className="meta-value">{artistEmailAddress}</span>
                    </div>
                    <div className="meta-row">
                      <span className="meta-label">From:</span>
                      <span className="meta-value">VibrantCast &lt;support@vibrantcast.com&gt;</span>
                    </div>
                    <div className="meta-row">
                      <span className="meta-label">Subject:</span>
                      {isEditing ? (
                        <input 
                          type="text"
                          className="subject-input"
                          value={artistEmail.subject}
                          onChange={(e) => setArtistEmail({...artistEmail, subject: e.target.value})}
                        />
                      ) : (
                        <span className="meta-value subject">{artistEmail.subject}</span>
                      )}
                    </div>
                  </div>

                  <div className="email-body-preview">
                    <div className="email-header-section celebration">
                      {isEditing ? (
                        <>
                          <input 
                            type="text"
                            className="header-input"
                            value={artistEmail.header}
                            onChange={(e) => setArtistEmail({...artistEmail, header: e.target.value})}
                            style={{ fontSize: '1.5rem', fontWeight: 'bold', textAlign: 'center', width: '100%', marginBottom: '0.5rem' }}
                          />
                          <p className="subtitle">You made a sale!</p>
                        </>
                      ) : (
                        <>
                          <h1>{artistEmail.header}</h1>
                          <p className="subtitle">You made a sale!</p>
                        </>
                      )}
                    </div>

                    {isEditing ? (
                      <textarea 
                        className="message-textarea"
                        value={artistEmail.greeting}
                        onChange={(e) => setArtistEmail({...artistEmail, greeting: e.target.value})}
                        rows={2}
                      />
                    ) : (
                      <p>{artistEmail.greeting}</p>
                    )}

                    <div className="order-details-table">
                      <h3>Sale Details</h3>
                      <table>
                        <tbody>
                          <tr>
                            <td className="label">Artwork:</td>
                            <td className="value bold">{order.product_name}</td>
                          </tr>
                          <tr>
                            <td className="label">Type:</td>
                            <td className="value">{productLabel}</td>
                          </tr>
                          <tr>
                            <td className="label">Sale Amount:</td>
                            <td className="value bold">{formatCurrency(order.price)}</td>
                          </tr>
                          <tr>
                            <td className="label">Order #:</td>
                            <td className="value">{order.order_num}</td>
                          </tr>
                          <tr>
                            <td className="label">Buyer:</td>
                            <td className="value">{order.first_name}</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>

                    <div className="next-steps-section">
                      <h3>Next Steps</h3>
                      {isEditing ? (
                        <textarea 
                          className="message-textarea"
                          value={artistEmail.nextStepsMessage}
                          onChange={(e) => setArtistEmail({...artistEmail, nextStepsMessage: e.target.value})}
                          rows={3}
                        />
                      ) : (
                        <p>{artistEmail.nextStepsMessage}</p>
                      )}
                    </div>
                  </div>
                </>
              ) : (
                <div className="no-artist-warning">
                  <AlertCircle size={48} />
                  <h3>No Artist Email Available</h3>
                  <p>The artist email could not be found for this order. The artist notification will be skipped.</p>
                </div>
              )}
            </div>
          )}

          {/* Push Notifications Tab */}
          {activeTab === 'push' && (
            <div className="push-preview-container">
              {/* Buyer Push */}
              <div className="push-section">
                <div className="push-section-header">
                  <User size={18} />
                  <h3>Buyer Push Notification</h3>
                  {order.uuid ? (
                    <span className="status-badge enabled">Will Send</span>
                  ) : (
                    <span className="status-badge disabled">No User Account</span>
                  )}
                </div>
                
                <div className="phone-mockup">
                  <div className="phone-notch"></div>
                  <div className="push-notification">
                    <div className="push-icon">
                      <img src="/images/vc-icon.png" alt="VC" onError={(e) => { e.target.style.display = 'none' }} />
                    </div>
                    <div className="push-content">
                      {isEditing ? (
                        <>
                          <input 
                            type="text"
                            className="push-title-input"
                            value={pushNotifications.buyerTitle}
                            onChange={(e) => setPushNotifications({...pushNotifications, buyerTitle: e.target.value})}
                            placeholder="Notification Title"
                          />
                          <textarea 
                            className="push-message-input"
                            value={pushNotifications.buyerMessage}
                            onChange={(e) => setPushNotifications({...pushNotifications, buyerMessage: e.target.value})}
                            placeholder="Notification message..."
                            rows={2}
                          />
                        </>
                      ) : (
                        <>
                          <div className="push-title">{pushNotifications.buyerTitle}</div>
                          <div className="push-message">{pushNotifications.buyerMessage}</div>
                        </>
                      )}
                    </div>
                    <div className="push-time">now</div>
                  </div>
                </div>
              </div>

              {/* Artist Push */}
              <div className="push-section">
                <div className="push-section-header">
                  <Palette size={18} />
                  <h3>Artist Push Notification</h3>
                  {artistInfo?.uuid ? (
                    <span className="status-badge enabled">Will Send</span>
                  ) : (
                    <span className="status-badge disabled">No User Account</span>
                  )}
                </div>
                
                <div className="phone-mockup">
                  <div className="phone-notch"></div>
                  <div className="push-notification artist">
                    <div className="push-icon">
                      <img src="/images/vc-icon.png" alt="VC" onError={(e) => { e.target.style.display = 'none' }} />
                    </div>
                    <div className="push-content">
                      {isEditing ? (
                        <>
                          <input 
                            type="text"
                            className="push-title-input"
                            value={pushNotifications.artistTitle}
                            onChange={(e) => setPushNotifications({...pushNotifications, artistTitle: e.target.value})}
                            placeholder="Notification Title"
                          />
                          <textarea 
                            className="push-message-input"
                            value={pushNotifications.artistMessage}
                            onChange={(e) => setPushNotifications({...pushNotifications, artistMessage: e.target.value})}
                            placeholder="Notification message..."
                            rows={2}
                          />
                        </>
                      ) : (
                        <>
                          <div className="push-title">{pushNotifications.artistTitle}</div>
                          <div className="push-message">{pushNotifications.artistMessage}</div>
                        </>
                      )}
                    </div>
                    <div className="push-time">now</div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer with Send Button */}
        <div className="preview-modal-footer">
          <div className="send-summary">
            <div className="send-item">
              <Mail size={16} />
              <span>Buyer email to {order.email}</span>
              <Check size={14} className="check" />
            </div>
            {artistEmailAddress && (
              <div className="send-item">
                <Mail size={16} />
                <span>Artist email to {artistEmailAddress}</span>
                <Check size={14} className="check" />
              </div>
            )}
            {order.uuid && (
              <div className="send-item">
                <Bell size={16} />
                <span>Buyer push notification</span>
                <Check size={14} className="check" />
              </div>
            )}
          </div>
          
          <div className="footer-actions">
            <button className="cancel-btn" onClick={onClose} disabled={sending}>
              Cancel
            </button>
            <button 
              className="send-btn" 
              onClick={handleSend}
              disabled={sending}
            >
              {sending ? (
                <>
                  <span className="spinner"></span>
                  Sending...
                </>
              ) : (
                <>
                  <Send size={18} />
                  Send Notifications
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PurchaseConfirmationPreview;
